import React, { useState, useRef } from "react";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import FormField from "../../layouts/pages/account/components/FormField";
import Autocomplete from "@mui/material/Autocomplete";
import Card from "@mui/material/Card";

// Import Dialog components from MUI
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

// If using react-router-dom for navigation:
import { useNavigate } from "react-router-dom";

interface FormData {
    inquiryType: string;
    firstName: string;
    lastName: string;
    company: string;
    email: string;
    streetAddress: string;
    addressLine2: string;
    city: string;
    stateUS: string;
    application: string;
    additionalInfo: string;
    name: string;
    companyGeneral: string;
    emailGeneral: string;
    message: string;
}
declare global {
    interface Window { gtag: (...args: any[]) => void; }
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL

const ContactFormSection: React.FC = () => {
    const [formData, setFormData] = useState<FormData>({
        inquiryType: "New Noise Monitoring Service",
        firstName: "",
        lastName: "",
        company: "",
        email: "",
        streetAddress: "",
        addressLine2: "",
        city: "",
        stateUS: "",
        application: "",
        additionalInfo: "",
        name: "",
        companyGeneral: "",
        emailGeneral: "",
        message: "",
    });

    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const formRef = useRef<HTMLFormElement>(null);
    const navigate = useNavigate();

    const handleInquiryTypeChange = (
        event: React.SyntheticEvent,
        newValue: string | null
    ): void => {
        if (newValue) {
            setFormData({ ...formData, inquiryType: newValue });
        }
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (formRef.current && !formRef.current.checkValidity()) {
            alert("Please fill in all required fields with valid information.");
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/account/contact`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData),
            });
            const data = await response.json();
            if (data.status === "success") {
                window.gtag('event', 'conversion', {
                    send_to: 'AW-17020587441/t-QvCNya9LoaELGbhrQ_'
                });
                // Open the modal on success
                setModalOpen(true);
            } else {
                alert("There was an error submitting the form. Please try again later.");
            }
        } catch (error) {
            console.error("Error submitting form:", error);
            alert("There was an error submitting the form. Please try again later.");
        }
    };

    // Array of US state abbreviations in alphabetical order
    const stateOptions = [
        "AK", "AL", "AR", "AZ", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID",
        "IL", "IN", "IA", "KS", "KY", "LA", "MA", "MD", "ME", "MI", "MN", "MO", "MS", "MT",
        "NC", "ND", "NE", "NH", "NJ", "NM", "NV", "NY", "OH", "OK", "OR", "PA", "RI",
        "SC", "SD", "TN", "TX", "UT", "VA", "VT", "WA", "WV", "WI", "WY"
    ];

    const handleModalClose = () => {
        setModalOpen(false);
        // Redirect the user after closing the modal.
        navigate("/");
        // Alternatively, if not using React Router:
        // window.location.href = "/thank-you";
    };

    return (
        <MDBox id="contact" py={10} px={{ xs: 2, sm: 4, md: 10 }} bgcolor="white">
            <MDTypography variant="h3" fontWeight="bold" textAlign="center" mb={4}>
                Contact Us
            </MDTypography>

            <Grid container spacing={4} justifyContent="center">
                <Card sx={{ p: 4, display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <form ref={formRef} onSubmit={handleSubmit}>
                        <Grid container spacing={2} justifyContent="center">
                            <Grid item xs={12} md={8}>
                                <FormControl fullWidth sx={{ mb: 4 }}>
                                    <Autocomplete
                                        value={formData.inquiryType}
                                        onChange={handleInquiryTypeChange}
                                        options={["New Noise Monitoring Service", "General Inquiry"]}
                                        renderInput={(params) => (
                                            <FormField
                                                {...params}
                                                label="Inquiry Type"
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        )}
                                    />
                                </FormControl>

                                {formData.inquiryType === "New Noise Monitoring Service" ? (
                                    <>
                                        <MDTypography variant="body1" mb={3}>
                                            We’re excited to hear more about your needs! Please fill out as much of the
                                            information below as possible so we can get back to you with details on how we can help.
                                        </MDTypography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <MDTypography variant="h6">Contact Information</MDTypography>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FormField
                                                    label="First Name"
                                                    placeholder="First"
                                                    required
                                                    value={formData.firstName}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                                                        setFormData({ ...formData, firstName: e.target.value })
                                                    }
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FormField
                                                    label="Last Name"
                                                    placeholder="Last"
                                                    required
                                                    value={formData.lastName}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                                                        setFormData({ ...formData, lastName: e.target.value })
                                                    }
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <FormField
                                                    label="Company"
                                                    placeholder="Company, Inc."
                                                    value={formData.company}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                                                        setFormData({ ...formData, company: e.target.value })
                                                    }
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <FormField
                                                    label="Email"
                                                    placeholder="example@email.com"
                                                    required
                                                    value={formData.email}
                                                    inputProps={{ type: "email" }}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                                                        setFormData({ ...formData, email: e.target.value })
                                                    }
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <MDTypography variant="h6">
                                                    Noise Monitoring Service Location
                                                </MDTypography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <FormField
                                                    label="Street Address"
                                                    placeholder="123 Main Street"
                                                    value={formData.streetAddress}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                                                        setFormData({ ...formData, streetAddress: e.target.value })
                                                    }
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <FormField
                                                    label="Address Line 2"
                                                    placeholder="Suite 22"
                                                    value={formData.addressLine2}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                                                        setFormData({ ...formData, addressLine2: e.target.value })
                                                    }
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <FormField
                                                    label="City"
                                                    placeholder="Enter City"
                                                    value={formData.city}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                                                        setFormData({ ...formData, city: e.target.value })
                                                    }
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <Autocomplete
                                                    options={stateOptions}
                                                    value={formData.stateUS}
                                                    onChange={(event, newValue) =>
                                                        setFormData({ ...formData, stateUS: newValue || "" })
                                                    }
                                                    renderInput={(params) => (
                                                        <FormField
                                                            {...params}
                                                            label="State"
                                                            InputLabelProps={{ shrink: true }}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Autocomplete
                                                    options={[
                                                        "Construction",
                                                        "Fitness",
                                                        "Traffic",
                                                        "Environmental",
                                                        "Performance Venue",
                                                        "Other",
                                                    ]}
                                                    value={formData.application}
                                                    onChange={(event, newValue) =>
                                                        setFormData({ ...formData, application: newValue || "" })
                                                    }
                                                    renderInput={(params) => (
                                                        <FormField
                                                            {...params}
                                                            label="Application"
                                                            InputLabelProps={{ shrink: true }}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <FormField
                                                    label="Additional Information"
                                                    multiline
                                                    rows={5}
                                                    fullWidth
                                                    value={formData.additionalInfo}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                                                        setFormData({ ...formData, additionalInfo: e.target.value })
                                                    }
                                                />
                                            </Grid>
                                        </Grid>
                                    </>
                                ) : (
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <MDInput
                                                label="Name"
                                                fullWidth
                                                required
                                                value={formData.name}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                                                    setFormData({ ...formData, name: e.target.value })
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <MDInput
                                                label="Company"
                                                fullWidth
                                                value={formData.companyGeneral}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                                                    setFormData({ ...formData, companyGeneral: e.target.value })
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <MDInput
                                                label="Email Address"
                                                type="email"
                                                fullWidth
                                                required
                                                value={formData.emailGeneral}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                                                    setFormData({ ...formData, emailGeneral: e.target.value })
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <MDInput
                                                label="Message"
                                                multiline
                                                rows={4}
                                                fullWidth
                                                value={formData.message}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                                                    setFormData({ ...formData, message: e.target.value })
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                )}
                                <MDBox mt={4} textAlign="center">
                                    <MDButton type="submit" color="info" size="large">
                                        Submit
                                    </MDButton>
                                </MDBox>
                            </Grid>
                        </Grid>
                    </form>
                </Card>
            </Grid>

            {/* Modal Dialog for the success message */}
            <Dialog open={modalOpen} onClose={handleModalClose}>
                <DialogTitle>Thank You!</DialogTitle>
                <DialogContent>
                    <MDTypography>
                        Your information has been received.
                    </MDTypography>
                </DialogContent>
                <DialogActions>
                    <MDButton color="info" onClick={handleModalClose}>
                        OK
                    </MDButton>
                </DialogActions>
            </Dialog>
        </MDBox>
    );
};

export default ContactFormSection;
